/**
 * Next Step Store
 * 
 * Manages persisted Next-Step Engine outputs.
 * CTAs triggered by exec acceptance are part of the auditable artifact trail.
 * 
 * Per Build Contract:
 * - Persist Next-Step Engine outputs by adding a next_steps table
 * - Write a Next-Step record on Accept (provisional)
 * - UI can compute current blockers at render time but must not invent CTAs client-side
 * 
 * The UI fetches persisted next steps and computes current gate blockers
 * at render time to show the CTA with up-to-date blocking status.
 */

import { create } from 'zustand';
import { getSafeSupabaseClient, generateUUID } from '../../lib/supabaseSync';

// ============================================================================
// Types
// ============================================================================

export type NextStepSourceEvent = 
  | 'CATCHBALL_ACCEPT_PROVISIONAL'
  | 'CATCHBALL_RETURN'
  | 'CATCHBALL_REJECT'
  | 'PHASE_TRANSITION'
  | 'GATE_UNBLOCKED'
  | 'MANUAL';

export type SuggestedAction = 
  | 'SELECT_OR_REFINE_CANDIDATE_POOL'
  | 'LOCK_FOCUS_LIST'
  | 'DEFINE_CRITERIA_SCALES'
  | 'LOCK_CRITERIA'
  | 'ENTER_VALUES'
  | 'RUN_CONVERGENCE'
  | 'PREPARE_ARRANGE'
  | 'CONTINUE_ARRANGE'
  | 'START_RANKING';

export type SuggestedPhase = 'DECIDE' | 'ENGAGE' | 'GATHER' | 'ARRANGE' | 'PRIORITIZE';

export interface NextStep {
  id: string;
  cycleId: string;
  proposalPacketId?: string;
  objectiveIds?: string[];
  sourceEvent: NextStepSourceEvent;
  suggestedAction: string;
  suggestedPhase?: SuggestedPhase;
  blockedReasonsSnapshot?: Record<string, unknown>;
  createdBy?: string;
  createdAt: Date;
  resolvedAt?: Date;
  resolvedBy?: string;
  resolutionAction?: string;
  workspaceId?: string;
  tenantId?: string;
}

export interface NextStepWithBlockers extends NextStep {
  // Computed at render time - current gate blockers
  currentBlockers: GateBlocker[];
  isBlocked: boolean;
}

export interface GateBlocker {
  type: 'criteria_lock' | 'scale_definition' | 'value_entry' | 'focus_list' | 'phase_position';
  description: string;
  remediationPath?: string;  // URL to fix the issue
  severity: 'hard' | 'soft';  // Hard = must fix, Soft = warning
}

// ============================================================================
// Store State
// ============================================================================

interface NextStepStoreState {
  nextSteps: NextStep[];
  isLoading: boolean;
  error: string | null;
  
  // ========================================================================
  // CRUD
  // ========================================================================
  
  /**
   * Fetch next steps for a cycle.
   */
  fetchNextStepsForCycle: (cycleId: string) => Promise<NextStep[]>;
  
  /**
   * Fetch next steps for a specific proposal packet.
   */
  fetchNextStepsForProposal: (proposalPacketId: string) => Promise<NextStep[]>;
  
  /**
   * Get active (unresolved) next step for a cycle.
   */
  getActiveNextStep: (cycleId: string) => NextStep | undefined;
  
  /**
   * Get active next step for a proposal.
   */
  getActiveNextStepForProposal: (proposalPacketId: string) => NextStep | undefined;
  
  /**
   * Resolve a next step (mark as actioned).
   */
  resolveNextStep: (nextStepId: string, actorId: string, resolutionAction?: string) => Promise<boolean>;
  
  /**
   * Create a manual next step.
   */
  createNextStep: (params: {
    cycleId: string;
    proposalPacketId?: string;
    objectiveIds?: string[];
    sourceEvent: NextStepSourceEvent;
    suggestedAction: string;
    suggestedPhase?: SuggestedPhase;
    blockedReasonsSnapshot?: Record<string, unknown>;
    createdBy: string;
  }) => Promise<NextStep | null>;
  
  // ========================================================================
  // Gate-Aware Computation
  // ========================================================================
  
  /**
   * Compute current blockers for a next step based on cycle gate state.
   * This is called at render time to get up-to-date blocking status.
   */
  computeBlockersForNextStep: (nextStep: NextStep) => Promise<GateBlocker[]>;
  
  /**
   * Get next step with current blockers computed.
   */
  getNextStepWithBlockers: (cycleId: string) => Promise<NextStepWithBlockers | null>;
  
  // ========================================================================
  // Utilities
  // ========================================================================
  
  clearError: () => void;
}

// ============================================================================
// DB Row Mapping
// ============================================================================

function mapRowToNextStep(row: Record<string, unknown>): NextStep {
  return {
    id: row.id as string,
    cycleId: row.cycle_id as string,
    proposalPacketId: row.proposal_packet_id as string | undefined,
    objectiveIds: row.objective_ids as string[] | undefined,
    sourceEvent: row.source_event as NextStepSourceEvent,
    suggestedAction: row.suggested_action as string,
    suggestedPhase: row.suggested_phase as SuggestedPhase | undefined,
    blockedReasonsSnapshot: row.blocked_reasons_snapshot as Record<string, unknown> | undefined,
    createdBy: row.created_by as string | undefined,
    createdAt: new Date(row.created_at as string),
    resolvedAt: row.resolved_at ? new Date(row.resolved_at as string) : undefined,
    resolvedBy: row.resolved_by as string | undefined,
    resolutionAction: row.resolution_action as string | undefined,
    workspaceId: row.workspace_id as string | undefined,
    tenantId: row.tenant_id as string | undefined,
  };
}

// ============================================================================
// Store Implementation
// ============================================================================

export const useNextStepStore = create<NextStepStoreState>()((set, get) => ({
  nextSteps: [],
  isLoading: false,
  error: null,

  // ========================================================================
  // CRUD
  // ========================================================================

  fetchNextStepsForCycle: async (cycleId: string) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return [];

    set({ isLoading: true, error: null });

    try {
      const { data, error } = await supabase
        .from('next_steps')
        .select('*')
        .eq('cycle_id', cycleId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('[NextStepStore] Fetch error:', error);
        set({ isLoading: false, error: error.message });
        return [];
      }

      const nextSteps = (data || []).map(mapRowToNextStep);
      
      // Merge into state
      set(state => {
        const otherSteps = state.nextSteps.filter(s => s.cycleId !== cycleId);
        return { 
          nextSteps: [...otherSteps, ...nextSteps],
          isLoading: false,
          error: null,
        };
      });

      console.log(`[NextStepStore] Fetched ${nextSteps.length} next steps for cycle ${cycleId}`);
      return nextSteps;
    } catch (err) {
      console.error('[NextStepStore] Fetch exception:', err);
      set({ isLoading: false, error: String(err) });
      return [];
    }
  },

  fetchNextStepsForProposal: async (proposalPacketId: string) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return [];

    try {
      const { data, error } = await supabase
        .from('next_steps')
        .select('*')
        .eq('proposal_packet_id', proposalPacketId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('[NextStepStore] Fetch for proposal error:', error);
        return [];
      }

      const nextSteps = (data || []).map(mapRowToNextStep);
      
      // Merge into state
      set(state => {
        const otherSteps = state.nextSteps.filter(s => s.proposalPacketId !== proposalPacketId);
        return { nextSteps: [...otherSteps, ...nextSteps] };
      });

      return nextSteps;
    } catch (err) {
      console.error('[NextStepStore] Fetch for proposal exception:', err);
      return [];
    }
  },

  getActiveNextStep: (cycleId: string) => {
    return get().nextSteps.find(s => 
      s.cycleId === cycleId && 
      !s.resolvedAt
    );
  },

  getActiveNextStepForProposal: (proposalPacketId: string) => {
    return get().nextSteps.find(s => 
      s.proposalPacketId === proposalPacketId && 
      !s.resolvedAt
    );
  },

  resolveNextStep: async (nextStepId: string, actorId: string, resolutionAction?: string) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return false;

    try {
      const now = new Date();

      const { error } = await supabase
        .from('next_steps')
        .update({
          resolved_at: now.toISOString(),
          resolved_by: actorId,
          resolution_action: resolutionAction,
        })
        .eq('id', nextStepId);

      if (error) {
        console.error('[NextStepStore] Resolve error:', error);
        return false;
      }

      // Update local state
      set(state => ({
        nextSteps: state.nextSteps.map(s =>
          s.id === nextStepId
            ? { ...s, resolvedAt: now, resolvedBy: actorId, resolutionAction }
            : s
        ),
      }));

      console.log(`[NextStepStore] Resolved next step ${nextStepId}`);
      return true;
    } catch (err) {
      console.error('[NextStepStore] Resolve exception:', err);
      return false;
    }
  },

  createNextStep: async (params) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return null;

    try {
      const now = new Date();
      const nextStep: Partial<Record<string, unknown>> = {
        id: generateUUID(),
        cycle_id: params.cycleId,
        proposal_packet_id: params.proposalPacketId,
        objective_ids: params.objectiveIds,
        source_event: params.sourceEvent,
        suggested_action: params.suggestedAction,
        suggested_phase: params.suggestedPhase,
        blocked_reasons_snapshot: params.blockedReasonsSnapshot,
        created_by: params.createdBy,
        created_at: now.toISOString(),
      };

      const { data, error } = await supabase
        .from('next_steps')
        .insert(nextStep)
        .select()
        .single();

      if (error) {
        console.error('[NextStepStore] Create error:', error);
        return null;
      }

      const createdStep = mapRowToNextStep(data);
      
      set(state => ({
        nextSteps: [createdStep, ...state.nextSteps],
      }));

      console.log(`[NextStepStore] Created next step ${createdStep.id}`);
      return createdStep;
    } catch (err) {
      console.error('[NextStepStore] Create exception:', err);
      return null;
    }
  },

  // ========================================================================
  // Gate-Aware Computation
  // ========================================================================

  computeBlockersForNextStep: async (nextStep: NextStep): Promise<GateBlocker[]> => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return [];

    const blockers: GateBlocker[] = [];

    try {
      // Fetch current cycle progress to compute blockers
      const { data: progressData } = await supabase
        .from('cycle_progress')
        .select('*')
        .eq('cycle_id', nextStep.cycleId)
        .single();

      if (!progressData) {
        return blockers;
      }

      const progress = progressData as Record<string, unknown>;

      // Check phase position
      const currentPhase = progress.current_phase as string;
      if (nextStep.suggestedPhase === 'ARRANGE' && currentPhase !== 'ARRANGE') {
        blockers.push({
          type: 'phase_position',
          description: `Cycle is in ${currentPhase} phase. Must advance to ARRANGE first.`,
          remediationPath: `/prioritization/processes/${nextStep.cycleId}`,
          severity: 'hard',
        });
      }

      // Check criteria lock
      if (!progress.criteria_locked) {
        blockers.push({
          type: 'criteria_lock',
          description: 'Criteria set must be locked before proceeding.',
          remediationPath: `/prioritization/processes/${nextStep.cycleId}?tab=gather`,
          severity: 'hard',
        });
      }

      // Check missing scales
      const missingScales = progress.criteria_missing_scales as string[] || [];
      if (missingScales.length > 0) {
        blockers.push({
          type: 'scale_definition',
          description: `${missingScales.length} criterion scale(s) need definition: ${missingScales.slice(0, 3).join(', ')}${missingScales.length > 3 ? '...' : ''}`,
          remediationPath: `/prioritization/processes/${nextStep.cycleId}?tab=gather`,
          severity: 'hard',
        });
      }

      // Check focus list status for ARRANGE actions
      if (nextStep.suggestedAction === 'SELECT_OR_REFINE_CANDIDATE_POOL') {
        const hasLockedFocusList = progress.focus_list_locked || false;
        if (!hasLockedFocusList && !progress.has_focus_list) {
          // This is expected - we're telling them to create one
        }
      }

      return blockers;
    } catch (err) {
      console.error('[NextStepStore] Compute blockers exception:', err);
      return blockers;
    }
  },

  getNextStepWithBlockers: async (cycleId: string): Promise<NextStepWithBlockers | null> => {
    const activeStep = get().getActiveNextStep(cycleId);
    if (!activeStep) return null;

    const blockers = await get().computeBlockersForNextStep(activeStep);

    return {
      ...activeStep,
      currentBlockers: blockers,
      isBlocked: blockers.some(b => b.severity === 'hard'),
    };
  },

  clearError: () => set({ error: null }),
}));

// ============================================================================
// Action Label Helpers
// ============================================================================

export function getActionLabel(action: string): string {
  const labels: Record<string, string> = {
    'SELECT_OR_REFINE_CANDIDATE_POOL': 'Select Item Pool',
    'LOCK_FOCUS_LIST': 'Lock Item Pool',
    'DEFINE_CRITERIA_SCALES': 'Define Criteria Scales',
    'LOCK_CRITERIA': 'Lock Criteria Set',
    'ENTER_VALUES': 'Enter Values',
    'RUN_CONVERGENCE': 'Run Convergence Session',
    'PREPARE_ARRANGE': 'Prepare for Arrange',
    'CONTINUE_ARRANGE': 'Continue Arrange',
    'START_RANKING': 'Start Ranking Session',
  };
  return labels[action] || action;
}

export function getActionDescription(action: string, sourceEvent: string): string {
  if (sourceEvent === 'CATCHBALL_ACCEPT_PROVISIONAL') {
    return 'Based on executive acceptance, proceed to candidate selection.';
  }
  if (sourceEvent === 'CATCHBALL_RETURN') {
    return 'Address the feedback and resubmit the proposal.';
  }
  
  const descriptions: Record<string, string> = {
    'SELECT_OR_REFINE_CANDIDATE_POOL': 'Select which items to include in the prioritization.',
    'LOCK_FOCUS_LIST': 'Lock the Item Pool to begin comparison.',
    'DEFINE_CRITERIA_SCALES': 'Define value scales for each criterion.',
    'LOCK_CRITERIA': 'Lock the criteria set to enable value entry.',
    'ENTER_VALUES': 'Enter attribute values for candidates.',
    'RUN_CONVERGENCE': 'Run a session to converge on values.',
    'PREPARE_ARRANGE': 'Complete readiness requirements for Arrange phase.',
    'CONTINUE_ARRANGE': 'Continue working in the Arrange phase.',
    'START_RANKING': 'Begin the ranking session.',
  };
  return descriptions[action] || 'Proceed with the next step.';
}
